/*
 * Project: bellcloud
 * File: apps/frontend/src/api/device/apiUnified.ts
 * Author: SWAN
 * Created: 2025-01-30
 * Description: Unified device API with automatic protocol detection
 */

import { apiHelpers, extractApiData } from '@/api/core';
import {
  generateUnifiedPacket as genUnifiedLocal,
  parseUnifiedPacket as parseUnifiedLocal,
} from '@packages/utils/device/devicePacketUnified';
import type {
  UnifiedGeneratePacketRequest,
  UnifiedGeneratePacketResponse,
  UnifiedPacketToJsonRequest,
  UnifiedPacketToJsonResponse,
} from '@packages/types/device/unified-dto';

export type {
  UnifiedGeneratePacketRequest,
  UnifiedGeneratePacketResponse,
  UnifiedPacketToJsonRequest,
  UnifiedPacketToJsonResponse,
} from '@packages/types/device/unified-dto';

/**
 * 통합 패킷 생성 (자동 프로토콜 감지)
 *
 * 순수 코덱(@packages/utils/device)을 브라우저에서 직접 실행한다. 서버 왕복 없음.
 * 영속성(device info/config 저장)은 savePacket 경로가 담당한다.
 */
export const generateUnifiedPacket = async (
  data: UnifiedGeneratePacketRequest
): Promise<UnifiedGeneratePacketResponse> => {
  const result = genUnifiedLocal({
    class_id: data.class_id,
    function_id: data.function_id,
    json_payload: data.json_payload,
    deviceInfo: data.deviceInfo,
    serialNumber: data.serialNumber,
    deviceName: data.deviceName,
    protocol: data.protocol,
  });
  return { packet: result.packet, protocol: result.protocol };
};

/**
 * 통합 패킷 파싱 (자동 프로토콜 감지)
 *
 * 순수 코덱을 브라우저에서 직접 실행한다. 서버 왕복 없음.
 */
export const parseUnifiedPacket = async (
  data: UnifiedPacketToJsonRequest
): Promise<UnifiedPacketToJsonResponse> => {
  const result = parseUnifiedLocal({
    packet: data.packet,
    serialNumber: data.serialNumber,
  });
  return {
    json: result.json,
    protocol: result.protocol,
    device_type: result.deviceType,
    class_id: result.class_id,
    function_id: result.function_id,
  };
};

/**
 * Get device configuration at a specific timestamp
 * Returns the latest configuration before or at the given timestamp
 */
interface GetConfigurationAtTimestampParams {
  serial_number: string;
  timestamp: string; // ISO 8601 format
  is_mini: boolean;
}

export const getConfigurationAtTimestamp = async (
  params: GetConfigurationAtTimestampParams
): Promise<any> => {
  const response = await apiHelpers.get<any>(
    'device.getConfigurationAtTimestamp',
    {},
    {
      serial_number: params.serial_number,
      timestamp: params.timestamp,
      is_mini: params.is_mini.toString(),
    }
  );
  return extractApiData(response);
};
